import { Col, Form, Input, InputNumber, Row } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSFormItemList from 'components/CIMSFormItemList'
import { HelpMessage } from 'utils/constants'
import { useAsphaltMixDesignsContext } from '../../AsphaltMixDesignsContext'
import { alphabeticalSort, listStringify } from 'shared'
import { AdditionalInfoProps, AdditionalInfoValues } from './types'
import CustomFieldEditor from 'components/CustomFields/CustomFieldEditor'
import CIMSSelect from 'components/CIMSSelect'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'

const AdditionalInfo: React.VFC<AdditionalInfoProps> = ({ hideBooleans }) => {
  const help = HelpMessage.Materials.Asphalt
  const { metadata } = useAsphaltMixDesignsContext()
  return (
    <>
      <Row gutter={16}>
        <Col flex={1}>
          <CIMSFormItem name="maxAggregateSize" label="Max Aggregate Size">
            <InputNumber style={{ width: '100%' }} />
          </CIMSFormItem>
        </Col>
        <Col flex={1}>
          <CIMSFormItem
            name="bulkSpecGravAgg"
            label="Specific Gravity of Aggregate"
          >
            <InputNumber style={{ width: '100%' }} />
          </CIMSFormItem>
        </Col>
      </Row>
      <CIMSFormItem name="materialSource" label="Material Source">
        <CIMSSelect
          options={useOptions({
            items: metadata.materialSources,
            label: 'source',
            sort: 'source',
            value: 'globalId',
          })}
          allowClear
        />
      </CIMSFormItem>
      <CIMSFormItemList
        name="percentages"
        help={help.TargetPercentages}
        label="Target"
        buttons={[
          {
            type: 'dropdown',
            icon: 'add',
            items: metadata.targets
              .map(({ id, name }) => ({ label: name, key: id }))
              .sort(alphabeticalSort('label')),
            loading: metadata.loading,
            add: (key) => {
              const found = metadata.targets.find(({ id }) => id === key)
              if (!found) return undefined
              return {
                targetId: found.id,
                name: found.name,
                percentage: null,
              }
            },
          },
        ]}
        validate={(value: AdditionalInfoValues['percentages']) => {
          if (!value) return true
          const nameNames = value.reduce(
            (map, { name }) => map.set(name, [...(map.get(name) ?? []), name]),
            new Map<string, string[]>()
          )
          const duplicates = [...nameNames.entries()].reduce(
            (names, [key, values]) => {
              if (values.length > 1) names.push(key)
              return names
            },
            Array<string>()
          )
          if (duplicates.length > 0) {
            const names = listStringify({ items: duplicates })
            return `Duplicate Targets are not allowed. Please remove extra lines for: ${names}`
          }
          return true
        }}
      >
        {(name) => (
          <>
            <Col flex={3}>
              <Form.Item hidden name={[name, 'targetId']}>
                <Input />
              </Form.Item>
              <CIMSFormItem
                help={HelpMessage.Name}
                label="Name"
                name={[name, 'name']}
              >
                <Input disabled />
              </CIMSFormItem>
            </Col>
            <Col flex={1}>
              <CIMSFormItem
                help={help.Percentage}
                label="Percentage"
                name={[name, 'percentage']}
                requiredMessage="Please enter a percentage"
                customValidator={{
                  message: 'Please enter a value between 0 and 100',
                  validator: (value) => value >= 0 && value <= 100,
                }}
              >
                <InputNumber style={{ width: '100%' }} />
              </CIMSFormItem>
            </Col>
          </>
        )}
      </CIMSFormItemList>
      <CustomFieldEditor alreadyDisabled={false} hideBooleans={hideBooleans} />
    </>
  )
}

export default AdditionalInfo
